import { CalendarClock, CalendarDays, Copy, Download, Home, MoreHorizontal, Plus, Settings, ShieldAlert, SlidersHorizontal, Trash2, Video } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatMeetingId } from '../../shared/protocol';
import { Brand } from '../components/Brand';
import { Avatar, Button, MenuItem, Popover } from '../components/ui';
import { api, joinUrl, loadClientConfig, shareBase } from '../lib/api';
import { formatDateTime, formatDuration } from '../lib/format';
import { copyWithFeedback } from '../lib/share';
import { downloadIcs, inviteText } from '../lib/invite';
import { navigate } from '../lib/router';
import { useSettings } from '../lib/settings';
import { getOwnedMeetings, getRecentMeetings, removeOwnedMeeting, saveOwnedMeeting, type OwnedMeeting } from '../lib/storage';
import { toast } from '../lib/toast';
import { SettingsDialog, type SettingsTab } from '../settings/SettingsDialog';
import { JoinDialog, ScheduleDialog } from './dialogs';
import '../styles/home.css';

type Dialog = { kind: 'join' } | { kind: 'schedule' } | { kind: 'settings'; tab?: SettingsTab } | null;

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function HomePage() {
  const settings = useSettings();
  const now = useClock();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [owned, setOwned] = useState<OwnedMeeting[]>(getOwnedMeetings);
  const [creating, setCreating] = useState(false);
  const recent = getRecentMeetings();

  const [base, setBase] = useState(shareBase);
  useEffect(() => {
    document.title = 'Nexo Meet';
    loadClientConfig()
      .then(() => setBase(shareBase()))
      .catch(() => undefined);
  }, []);
  const secure = window.isSecureContext;

  const upcoming = owned
    .filter((m) => m.startTime && Date.parse(m.startTime) + m.durationMinutes * 60_000 > now.getTime())
    .sort((a, b) => Date.parse(a.startTime!) - Date.parse(b.startTime!));

  const newMeeting = async () => {
    setCreating(true);
    try {
      const res = await api.createMeeting({ title: settings.displayName ? `Reunión de ${settings.displayName}` : 'Reunión instantánea' });
      saveOwnedMeeting({
        id: res.meeting.id,
        hostKey: res.hostKey,
        passcode: res.passcode,
        title: res.meeting.title,
        startTime: null,
        durationMinutes: res.meeting.durationMinutes,
        createdAt: res.meeting.createdAt,
      });
      navigate(`/j/${res.meeting.id}${res.passcode ? `?pwd=${encodeURIComponent(res.passcode)}` : ''}`);
    } catch (err) {
      toast((err as Error).message, 'error');
      setCreating(false);
    }
  };

  return (
    <div className="home">
      <header className="topnav">
        <Brand />
        <nav className="topnav-tabs" aria-label="Secciones">
          <button type="button" className="topnav-tab" aria-current="page">
            <Home size={16} /> Inicio
          </button>
        </nav>
        <div className="topnav-spacer" />
        <button type="button" className="icon-btn" aria-label="Configuración" title="Configuración" onClick={() => setDialog({ kind: 'settings' })}>
          <Settings size={18} />
        </button>
        <button type="button" className="topnav-user" onClick={() => setDialog({ kind: 'settings', tab: 'general' })} aria-label="Perfil y preferencias">
          <span>{settings.displayName || 'Configura tu nombre'}</span>
          <Avatar name={settings.displayName || '?'} size={30} />
        </button>
      </header>

      <main className="home-main">
        <section aria-labelledby="home-title">
          <div className="home-greeting">
            <h1 id="home-title">{settings.displayName ? `Hola, ${settings.displayName.split(' ')[0]}` : 'Bienvenido a Nexo Meet'}</h1>
            <p>Crea una reunión al instante, únete con un código o programa la próxima.</p>
          </div>
          {!secure && (
            <div className="callout callout-error home-callout" role="alert">
              <ShieldAlert size={18} />
              <span>
                Esta página se abrió sin HTTPS, así que el navegador no permitirá usar la cámara ni el micrófono.
                {base.startsWith('https://') ? (
                  <>
                    {' '}
                    Abre <a href={base}>{base}</a>.
                  </>
                ) : (
                  ' Arranca Nexo con «npm run lan» o «npm run publico».'
                )}
              </span>
            </div>
          )}
          <div className="action-grid">
            <button type="button" className="action-tile orange" onClick={newMeeting} disabled={creating}>
              <span className="action-icon">
                <Video size={34} />
              </span>
              {creating ? 'Creando…' : 'Nueva reunión'}
              <span className="sub">Empieza ahora mismo</span>
            </button>
            <button type="button" className="action-tile" onClick={() => setDialog({ kind: 'join' })}>
              <span className="action-icon">
                <Plus size={36} />
              </span>
              Unirse
              <span className="sub">Con ID o enlace</span>
            </button>
            <button type="button" className="action-tile" onClick={() => setDialog({ kind: 'schedule' })}>
              <span className="action-icon">
                <CalendarDays size={32} />
              </span>
              Programar
              <span className="sub">Para más tarde</span>
            </button>
            <button type="button" className="action-tile" onClick={() => setDialog({ kind: 'settings', tab: 'video' })}>
              <span className="action-icon">
                <SlidersHorizontal size={32} />
              </span>
              Probar audio y vídeo
              <span className="sub">Cámara, micrófono y altavoz</span>
            </button>
          </div>
          {secure && base !== location.origin && (
            <p className="share-address">
              Otros dispositivos entran en <strong>{base}</strong>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copyWithFeedback(base, 'dirección')}>
                <Copy size={14} /> Copiar
              </button>
            </p>
          )}
        </section>

        <aside className="side-card" aria-label="Agenda">
          <div className="clock-hero">
            <div className="time">{now.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</div>
            <div className="date">{now.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          <div className="side-section">
            <h2>
              Próximas reuniones
              <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: 'schedule' })}>
                <Plus size={14} /> Programar
              </Button>
            </h2>
            {upcoming.length === 0 ? (
              <div className="empty">
                <CalendarClock size={36} />
                <span>No tienes reuniones programadas.</span>
              </div>
            ) : (
              upcoming.map((m) => <UpcomingRow key={m.id} meeting={m} now={now.getTime()} onDeleted={() => setOwned(getOwnedMeetings())} hostName={settings.displayName} />)
            )}
          </div>
          {recent.length > 0 && (
            <div className="side-section">
              <h2>Recientes</h2>
              <div className="recent-list">
                {recent.map((r) => (
                  <button key={r.id} type="button" className="chip" onClick={() => navigate(`/j/${r.id}`)} title={`Volver a entrar en ${r.title}`}>
                    <Video size={14} />
                    <span>{r.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </main>

      {dialog?.kind === 'join' && <JoinDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'schedule' && <ScheduleDialog onClose={() => setDialog(null)} onCreated={() => setOwned(getOwnedMeetings())} />}
      {dialog?.kind === 'settings' && <SettingsDialog initialTab={dialog.tab} onClose={() => setDialog(null)} />}
    </div>
  );
}

function UpcomingRow({ meeting, now, onDeleted, hostName }: { meeting: OwnedMeeting; now: number; onDeleted: () => void; hostName: string }) {
  const [menu, setMenu] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const start = Date.parse(meeting.startTime!);
  const live = start <= now;
  const source = { ...meeting, startTime: meeting.startTime! };

  const remove = async () => {
    setMenu(false);
    if (!confirm(`¿Eliminar «${meeting.title}»? Los enlaces de invitación dejarán de funcionar.`)) return;
    try {
      await api.deleteMeeting(meeting.id, meeting.hostKey);
    } catch (err) {
      // A meeting already removed on the server can still be cleaned up locally.
      if ((err as { status?: number }).status !== 404) return void toast((err as Error).message, 'error');
    }
    removeOwnedMeeting(meeting.id);
    onDeleted();
    toast('Reunión eliminada', 'success');
  };

  return (
    <div className="meeting-row">
      <div className={`meeting-when ${live ? 'live' : ''}`}>{live ? 'En curso' : formatDateTime(meeting.startTime!)}</div>
      <div className="meeting-info">
        <strong>{meeting.title}</strong>
        <span>
          ID {formatMeetingId(meeting.id)} · {formatDuration(meeting.durationMinutes)}
        </span>
      </div>
      <div className="meeting-actions">
        <Button size="sm" variant="primary" onClick={() => navigate(`/j/${meeting.id}${meeting.passcode ? `?pwd=${encodeURIComponent(meeting.passcode)}` : ''}`)}>
          Iniciar
        </Button>
        <button ref={moreRef} type="button" className="icon-btn" aria-label={`Más opciones de ${meeting.title}`} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
          <MoreHorizontal size={18} />
        </button>
        {menu && (
          <Popover anchor={moreRef} onClose={() => setMenu(false)} placement="bottom" label="Opciones de la reunión">
            <MenuItem
              icon={<Copy size={16} />}
              onSelect={async () => {
                setMenu(false);
                await copyWithFeedback(inviteText(source, hostName || undefined), 'invitación');
              }}
            >
              Copiar invitación
            </MenuItem>
            <MenuItem
              icon={<Copy size={16} />}
              onSelect={async () => {
                setMenu(false);
                await copyWithFeedback(joinUrl(meeting.id, meeting.passcode), 'enlace');
              }}
            >
              Copiar enlace
            </MenuItem>
            <MenuItem
              icon={<Download size={16} />}
              onSelect={() => {
                setMenu(false);
                downloadIcs(source);
              }}
            >
              Añadir al calendario
            </MenuItem>
            <div className="menu-sep" />
            <MenuItem icon={<Trash2 size={16} />} danger onSelect={remove}>
              Eliminar
            </MenuItem>
          </Popover>
        )}
      </div>
    </div>
  );
}
